import type { EmployeePermissionsList } from '@agos/api-client/model'
import { effectiveRoleKey, formatCount, T, tt } from '@agos/shared'
import { Spinner, StatusBadge } from '@agos/ui'
import { useMemo } from 'react'

import { allowedOf, roleKeyOf, type RolePerson, type RolesOverview } from '../../roles/roles-api'
import { heldBecause, isBuiltInAdministrator, type EmployeePermissions, type HeldBecause } from '../permission-matrix'
import { roleNameOf } from '../role-model'
import { RoleChip } from '../RoleChip'
import { TabReadFailed } from '../TabReadFailed'
import { OverridesEditor } from './OverridesEditor'
import { RolePicker } from './RolePicker'

/** The exceptions read, as far as the panel needs it - the query's own result fits. */
interface RightsRead {
  data: EmployeePermissionsList | undefined
  isError: boolean
  isLoading: boolean
  refetch: () => Promise<unknown>
}

/**
 * One person on *Ľudia* - their role and what changing it would do, *Čo smie a prečo*, and their exceptions.
 * `FRONTEND.md` §9.752 (design §2.9).
 *
 * **Held is always the server's `allowed`** (§10A.407's `allowedSet`): the reasons are derived beside it, never
 * instead of it, so this panel cannot tell a main administrator that somebody lacks a right the API gives them - which
 * the menu-based answer did for every built-in Administrátor (#58). *Čo smie a prečo* and the exceptions are the main
 * administrator's (owner question #165); a delegate gets the role picker alone.
 */
export function PersonPanel({
  person,
  overview,
  mainAdmin,
  rights,
}: {
  person: RolePerson
  overview: RolesOverview
  mainAdmin: boolean
  rights: RightsRead
}) {
  const roleKey = effectiveRoleKey(person)
  const catalogue = overview.allPermissions
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const row = rights.data?.employees.find((candidate) => candidate.id === person.id)
  /* The matrix's own shape, with §10A.408's two fields read off the generated row through `in` - so the swap to the
     regenerated model at integration changes nothing here. */
  const selected: EmployeePermissions | undefined =
    row === undefined ? undefined : { ...row, roleKey: roleKeyOf(row), allowed: allowedOf(row) }

  return (
    <div className="space-y-3" data-person-panel={person.id}>
      <div className="rounded-2xl border border-border bg-card p-4 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-bold" data-person-name>
            {person.name}
          </h2>
          <RoleChip roleKey={roleKey} roles={overview.roles} size="md" />
          {person.alsoDrives && (
            <span className="text-[11px] font-semibold text-amber-500" data-person-also-drives>
              + {tt('rozvoz')}
            </span>
          )}
        </div>
        {(person.position ?? '').trim() !== '' && (
          <p className="text-xs text-muted-foreground">{person.position}</p>
        )}
      </div>

      <section className="rounded-2xl border border-border bg-card p-4 space-y-2" data-person-role>
        <h3 className="text-sm font-bold">{tt('Rola')}</h3>
        <RolePicker person={person} overview={overview} />
      </section>

      {mainAdmin &&
        (rights.isError ? (
          <TabReadFailed what="overrides" retry={() => void rights.refetch()}>
            <T>
              Bez výnimiek nevieme povedať, čo tento človek smie, ani ich upraviť. Ukladanie výnimiek je preto vypnuté.
            </T>
          </TabReadFailed>
        ) : rights.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : selected === undefined ? (
          <p className="text-xs text-muted-foreground" data-person-not-in-rights>
            {tt('Tohto človeka zoznam výnimiek nepozná - je možno neaktívny.')}
          </p>
        ) : (
          <>
            <WhyHeld person={selected} catalogue={catalogue} known={known} />
            <OverridesEditor
              key={person.id}
              selected={selected}
              roleName={roleNameOf(overview.roles, roleKey)}
              catalogue={catalogue}
              readFailed={rights.isError}
            />
          </>
        ))}
    </div>
  )
}

/**
 * *Čo smie a prečo* - the reasons, counted, over what the server says the person holds.
 *
 * The exceptions editor below names the reason on every row (`data-held-because`); this is the summary that tells a
 * main administrator, before they read 128 rows, how much of it is the role, how much a family, how much an exception
 * and how much the built-in Administrátor's pass.
 */
function WhyHeld({
  person,
  catalogue,
  known,
}: {
  person: EmployeePermissions
  catalogue: readonly string[]
  known: ReadonlySet<string>
}) {
  const counts = new Map<HeldBecause, number>()
  for (const permission of catalogue) {
    const because = heldBecause(permission, person, known)
    counts.set(because, (counts.get(because) ?? 0) + 1)
  }
  const held = catalogue.length - (counts.get('no') ?? 0)
  const parts: { because: HeldBecause; label: string; color: 'blue' | 'violet' | 'emerald' | 'amber' | 'zinc' }[] = [
    { because: 'role', label: tt('z roly'), color: 'blue' },
    { because: 'parent', label: tt('cez nadradené'), color: 'violet' },
    { because: 'override', label: tt('výnimkou'), color: 'emerald' },
    { because: 'administrator-pass', label: tt('cez vstavaného Administrátora'), color: 'amber' },
    { because: 'no', label: tt('nemá'), color: 'zinc' },
  ]

  return (
    <section className="rounded-2xl border border-border bg-card p-4 space-y-2" data-person-why>
      <h3 className="text-sm font-bold">{tt('Čo smie a prečo')}</h3>
      <p className="text-xs" data-person-why-held={held}>
        {tt('Server mu dovolí')} <strong>{formatCount(held)}</strong> {tt('z')} {formatCount(catalogue.length)}.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {parts
          .filter((part) => (counts.get(part.because) ?? 0) > 0)
          .map((part) => (
            <span key={part.because} data-person-why-count={part.because}>
              <StatusBadge label={`${formatCount(counts.get(part.because) ?? 0)} ${part.label}`} color={part.color} />
            </span>
          ))}
      </div>
      {person.allowed == null && (
        <p className="text-[11px] text-muted-foreground" data-person-why-menu>
          {tt('Server zatiaľ neposiela, čo tomuto človeku dovolí - zobrazuje sa jeho menu.')}
        </p>
      )}
      {isBuiltInAdministrator(person) && (
        <p className="text-[11px] text-amber-500" data-person-why-pass>
          {tt('Vstavaný Administrátor prejde serverom aj na nezaškrtnuté práva, ak ich nemá výnimkou zakázané.')}
        </p>
      )}
    </section>
  )
}
